import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, MessageSquare, ArrowRight } from "lucide-react";
import {
  searchLeads,
  getLeadDisplayName,
  getLeadSubtitle,
} from "../../utils/crmLeadSearch";

const CrmGlobalSearch = ({
  open,
  onClose,
  leads = [],
  onSelectLead,
  onOpenMessages,
}) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(() => searchLeads(leads, query, 25), [leads, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter" && results[activeIndex]) {
        e.preventDefault();
        onSelectLead?.(results[activeIndex]);
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, results, activeIndex, onClose, onSelectLead]);

  if (!open) return null;

  return (
    <div className="crm-global-search" role="dialog" aria-modal="true" aria-label="Search CRM leads">
      <button type="button" className="crm-global-search__backdrop" onClick={onClose} aria-label="Close search" />
      <div className="crm-global-search__panel">
        <div className="crm-global-search__input-wrap">
          <Search size={18} className="crm-global-search__icon" />
          <input
            ref={inputRef}
            type="search"
            className="crm-global-search__input"
            placeholder="Search leads by name, phone, email, source, city…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search leads"
          />
          <kbd className="crm-global-search__kbd">Esc</kbd>
          <button type="button" className="crm-global-search__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="crm-global-search__results">
          {query.trim() === "" ? (
            <p className="crm-global-search__hint">Type to search across all loaded enquiries</p>
          ) : results.length === 0 ? (
            <p className="crm-global-search__hint">No leads match &ldquo;{query}&rdquo;</p>
          ) : (
            <ul className="crm-global-search__list">
              {results.map((lead, index) => (
                <li key={lead.id}>
                  <button
                    type="button"
                    className={`crm-global-search__item${index === activeIndex ? " crm-global-search__item--active" : ""}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      onSelectLead?.(lead);
                      onClose();
                    }}
                  >
                    <div className="crm-global-search__item-main">
                      <strong>{getLeadDisplayName(lead)}</strong>
                      <span>{getLeadSubtitle(lead)}</span>
                    </div>
                    <div className="crm-global-search__item-actions">
                      {onOpenMessages ? (
                        <span
                          className="crm-global-search__msg-btn"
                          role="button"
                          tabIndex={0}
                          title="Open messages"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenMessages(lead);
                            onClose();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              onOpenMessages(lead);
                              onClose();
                            }
                          }}
                        >
                          <MessageSquare size={14} />
                        </span>
                      ) : null}
                      <ArrowRight size={14} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="crm-global-search__footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> go to lead</span>
          <span><kbd>Ctrl</kbd><kbd>K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
};

export default CrmGlobalSearch;
